import type { SportType, RunIntensity } from '@/types';
import type { HistoryCachedPayload } from '@/types/historyPayload';

const STORAGE_KEY = 'p2t_last_workout_session_v1';
const STORAGE_VERSION = 1;

export type ResultsCacheShape = Record<string, HistoryCachedPayload>;

export interface PersistedWorkoutSession {
  v: number;
  lastKey: string;
  resultsCache: ResultsCacheShape;
}

export function parseCacheKeyToSportMeta(cacheKey: string): {
  sport: SportType;
  runIntensity?: RunIntensity;
} {
  if (cacheKey === 'bike') return { sport: 'bike' };
  if (cacheKey.startsWith('run-')) {
    const tail = cacheKey.slice(4) as RunIntensity;
    if (tail === 'pace' || tail === 'power' || tail === 'hr') {
      return { sport: 'run', runIntensity: tail };
    }
  }
  return { sport: 'bike' };
}

export function loadPersistedWorkoutSession(): PersistedWorkoutSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedWorkoutSession>;
    if (parsed.v !== STORAGE_VERSION || typeof parsed.lastKey !== 'string' || !parsed.resultsCache) {
      return null;
    }
    const entry = parsed.resultsCache[parsed.lastKey];
    if (!entry || !Array.isArray(entry.segments) || entry.segments.length === 0) {
      return null;
    }
    return {
      v: STORAGE_VERSION,
      lastKey: parsed.lastKey,
      resultsCache: parsed.resultsCache as ResultsCacheShape,
    };
  } catch {
    return null;
  }
}

export function savePersistedWorkoutSession(lastKey: string, resultsCache: ResultsCacheShape): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        v: STORAGE_VERSION,
        lastKey,
        resultsCache,
      } satisfies PersistedWorkoutSession),
    );
  } catch (e) {
    console.warn('[workoutSessionPersistence] Could not save session:', e);
  }
}
